/**
 * The seating marker on the SCENE (ADR-030 §14) — read and write the head
 * seating a file declares, on the seat bone's node. The marker lives in glTF
 * `extras`, which three carries as `Object3D.userData` in both directions
 * (GLTFLoader copies extras → userData; GLTFExporter serialises userData →
 * extras), so nothing here touches bytes: the export door writes the scene,
 * and the marker rides with the node. The record's shape and its parse are
 * the kernel's (head-space.ts); this module only resolves WHICH node — inside
 * the selected skeleton, the one rule every door applies.
 *
 * Why the marker exists: a seated bind cannot say who seated it. The
 * baseline instrument reads the bind alone, so a reopened export measured
 * the gap the seating had closed and re-seated it back — erasing a
 * declaration a person had pushed past the baseline (Allen's fox onesie,
 * 2026-08-27). The file must carry the intent; the bind carries the result.
 *
 * Fail-loud: an unknown bone, or one the selected skeleton does not bind,
 * throws — a caller bug. A MALFORMED marker refuses through the kernel parse
 * (HeadSpaceRefusal, naming the bone and the file): a file that carries a
 * marker it cannot mean is inconsistent, and reading it as "nothing declared"
 * would silently re-run the heuristic on a declared neck.
 *
 * Depends on: three, convert-rig (the selected-skeleton seam), kernel (the
 * marker's shape + parse). Depended on by: the app session driver
 * (run-document) and the app's in-place seating commit.
 */
import type { Object3D } from 'three';
import {
  parseHeadSeatingMarker,
  withHeadSeatingMarker,
  type HeadSeatingMarker,
} from '@harryhausen/kernel';
import { boneOfSelectedSkeleton } from './convert-rig.js';
import { AdapterError } from './errors.js';

export class SceneSeatingMarkerError extends AdapterError {}

interface Selection {
  readonly label: string;
  readonly selectSkeleton?: string;
}

function seatNode(root: Object3D, boneName: string, options: Selection): Object3D {
  const node = boneOfSelectedSkeleton(root, boneName, options);
  if (node === undefined) {
    throw new SceneSeatingMarkerError(
      `no seating marker door for "${boneName}" — not a bone of the selected skeleton ("${options.label}").`,
    );
  }
  return node;
}

/** The head seating the file declares on `boneName`, or null when it declares nothing. */
export function readSceneHeadSeatingMarker(
  root: Object3D,
  boneName: string,
  options: Selection,
): HeadSeatingMarker | null {
  const node = seatNode(root, boneName, options);
  return parseHeadSeatingMarker(node.userData, `"${boneName}" of "${options.label}"`);
}

/**
 * Declare (or, for null, withdraw) the head seating on `boneName`'s node so
 * the next export carries it. Every other userData key survives.
 */
export function writeSceneHeadSeatingMarker(
  root: Object3D,
  boneName: string,
  marker: HeadSeatingMarker | null,
  options: Selection,
): void {
  const node = seatNode(root, boneName, options);
  node.userData = withHeadSeatingMarker(node.userData, marker);
}
